import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { invalidateFor } from "@/lib/cache-registry";
import { useCrmAudit } from "@/hooks/use-crm-audit";
import type { DBCustomerMonitoringSource, MonitoringSourceKind, SignalFrequency } from "@/lib/db-types";

// Maks fire rader per kunde (én per kilde), så plain select uten paginering er trygt.

/** Overvåkningsrader for én kunde. Brønnøysund har ingen rad: den er alltid på. */
export function useMonitoringSources(customerId: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["monitoring-sources", customerId],
    queryFn: async (): Promise<DBCustomerMonitoringSource[]> => {
      const { data, error } = await supabase
        .from("customer_monitoring_sources")
        .select("*")
        .eq("customer_id", customerId!)
        .order("source", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DBCustomerMonitoringSource[];
    },
    enabled: !!customerId && (options.enabled ?? true),
    staleTime: 60 * 1000,
  });
}

const onError = (err: Error) => toast.error(`Kunne ikke lagre overvåkning: ${err.message}`);

/** Oppretter en kilde når rådgiver skriver inn adresse eller handle (identifier_source = manual). */
export function useUpsertMonitoringSource() {
  const qc = useQueryClient();
  const audit = useCrmAudit();
  return useMutation({
    mutationFn: async (vars: {
      customerId: string;
      source: MonitoringSourceKind;
      identifier: string;
      isActive?: boolean;
    }) => {
      const identifier = vars.identifier.trim();
      if (!identifier) throw new Error("Adresse eller handle mangler");
      const { data, error } = await supabase
        .from("customer_monitoring_sources")
        .insert({
          customer_id: vars.customerId,
          source: vars.source,
          identifier,
          identifier_source: "manual",
          is_active: vars.isActive ?? false,
          override: vars.isActive !== undefined,
          created_by: audit.authUserId,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerMonitoringSource;
    },
    onSuccess: async (row) => {
      audit.log({
        eventType: "monitoring_source.updated",
        entityType: "customer_monitoring_sources",
        entityId: row.customer_id,
        payload: { customer_id: row.customer_id, source: row.source, before: null, after: { is_active: row.is_active, override: row.override } },
      });
      await invalidateFor(qc, "customer_monitoring_sources", { customerId: row.customer_id });
    },
    onError,
  });
}

export interface MonitoringPatch {
  identifier?: string;
  frequency?: SignalFrequency;
}

/** Endrer adresse eller frekvens på en eksisterende kilde. */
export function useUpdateMonitoringSource() {
  const qc = useQueryClient();
  const audit = useCrmAudit();
  return useMutation({
    mutationFn: async (vars: { row: DBCustomerMonitoringSource; patch: MonitoringPatch }) => {
      const patch: MonitoringPatch = { ...vars.patch };
      if (patch.identifier !== undefined) {
        patch.identifier = patch.identifier.trim();
        if (!patch.identifier) throw new Error("Adresse eller handle mangler");
      }
      const { data, error } = await supabase
        .from("customer_monitoring_sources")
        .update(patch)
        .eq("id", vars.row.id)
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerMonitoringSource;
    },
    onSuccess: async (row, vars) => {
      audit.log({
        eventType: "monitoring_source.updated",
        entityType: "customer_monitoring_sources",
        entityId: row.customer_id,
        payload: {
          customer_id: row.customer_id,
          source: row.source,
          before: { frequency: vars.row.frequency },
          after: { frequency: row.frequency },
          // Selve adressen/handlen logges aldri.
          identifier_changed: row.identifier !== vars.row.identifier,
        },
      });
      await invalidateFor(qc, "customer_monitoring_sources", { customerId: row.customer_id });
    },
    onError,
  });
}

export interface MonitoredCustomerSource {
  customer_id: string;
  source: MonitoringSourceKind;
  customer: { name: string } | null;
}

/** Aktive kilder på tvers av kunder (prøvekjøring av agenter velger blant disse). Ubundet, derfor paginert. */
export function useMonitoredCustomers(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["monitored-customers"],
    queryFn: () =>
      fetchAllPaginated<MonitoredCustomerSource>(
        "customer_monitoring_sources",
        "customer_id, source, customer:customers!customer_monitoring_sources_customer_id_fkey(name)",
        (q) => q.eq("is_active", true),
      ),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}
